import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Alert,
} from '@mui/material'
import { ContentCopy, FileUpload } from '@mui/icons-material'
import { AdminThemeDto, CreateThemeRequest, ThemePaletteDto } from '@api/types/themes'
import { ThemeColorField } from './ThemeColorField'

interface ThemeFormDialogProps {
  open: boolean
  theme: AdminThemeDto | null // null = create mode
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (data: CreateThemeRequest) => void
}

const DEFAULT_LIGHT_PALETTE: ThemePaletteDto = {
  primary: '#1976d2',
  primaryLight: '#42a5f5',
  primaryDark: '#1565c0',
  primaryContrast: '#ffffff',
  secondary: '#9c27b0',
  secondaryLight: '#ba68c8',
  secondaryDark: '#7b1fa2',
  secondaryContrast: '#ffffff',
  backgroundDefault: '#ffffff',
  backgroundPaper: '#f5f5f5',
  textPrimary: '#212121',
  textSecondary: '#757575',
}

const DEFAULT_DARK_PALETTE: ThemePaletteDto = {
  primary: '#90caf9',
  primaryLight: '#e3f2fd',
  primaryDark: '#42a5f5',
  primaryContrast: '#121212',
  secondary: '#ce93d8',
  secondaryLight: '#f3e5f5',
  secondaryDark: '#ab47bc',
  secondaryContrast: '#121212',
  backgroundDefault: '#121212',
  backgroundPaper: '#1e1e1e',
  textPrimary: '#ffffff',
  textSecondary: '#b0b0b0',
}

const PALETTE_FIELDS: { key: keyof ThemePaletteDto; label: string; group: string }[] = [
  { key: 'primary', label: 'Primary', group: 'Primary' },
  { key: 'primaryLight', label: 'Primary Light', group: 'Primary' },
  { key: 'primaryDark', label: 'Primary Dark', group: 'Primary' },
  { key: 'primaryContrast', label: 'Primary Contrast', group: 'Primary' },
  { key: 'secondary', label: 'Secondary', group: 'Secondary' },
  { key: 'secondaryLight', label: 'Secondary Light', group: 'Secondary' },
  { key: 'secondaryDark', label: 'Secondary Dark', group: 'Secondary' },
  { key: 'secondaryContrast', label: 'Secondary Contrast', group: 'Secondary' },
  { key: 'backgroundDefault', label: 'Background Default', group: 'Background' },
  { key: 'backgroundPaper', label: 'Background Paper', group: 'Background' },
  { key: 'textPrimary', label: 'Text Primary', group: 'Text' },
  { key: 'textSecondary', label: 'Text Secondary', group: 'Text' },
]

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function ThemeFormDialog({ open, theme, saving, error, onClose, onSave }: ThemeFormDialogProps) {
  const isEdit = !!theme
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [sortOrder, setSortOrder] = useState(0)
  const [previewPrimary, setPreviewPrimary] = useState('#1976d2')
  const [previewSecondary, setPreviewSecondary] = useState('#9c27b0')
  const [lightPalette, setLightPalette] = useState<ThemePaletteDto>({ ...DEFAULT_LIGHT_PALETTE })
  const [darkPalette, setDarkPalette] = useState<ThemePaletteDto>({ ...DEFAULT_DARK_PALETTE })
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setImportError(null)
      if (theme) {
        setName(theme.name)
        setSlug(theme.slug)
        setSlugManuallyEdited(true)
        setDescription(theme.description)
        setIsDefault(theme.isDefault)
        setSortOrder(theme.sortOrder)
        setPreviewPrimary(theme.previewPrimary)
        setPreviewSecondary(theme.previewSecondary)
        setLightPalette({ ...theme.light })
        setDarkPalette({ ...theme.dark })
      } else {
        setName('')
        setSlug('')
        setSlugManuallyEdited(false)
        setDescription('')
        setIsDefault(false)
        setSortOrder(0)
        setPreviewPrimary('#1976d2')
        setPreviewSecondary('#9c27b0')
        setLightPalette({ ...DEFAULT_LIGHT_PALETTE })
        setDarkPalette({ ...DEFAULT_DARK_PALETTE })
      }
    }
  }, [open, theme])

  const applyImportedTheme = (data: Record<string, unknown>) => {
    if (data.name && typeof data.name === 'string') {
      setName(data.name)
      if (!slugManuallyEdited && data.slug && typeof data.slug === 'string') {
        setSlug(data.slug)
      }
    }
    if (data.slug && typeof data.slug === 'string') setSlug(data.slug)
    if (data.description && typeof data.description === 'string') setDescription(data.description)
    if (typeof data.isDefault === 'boolean') setIsDefault(data.isDefault)
    if (typeof data.sortOrder === 'number') setSortOrder(data.sortOrder)
    if (data.previewPrimary && typeof data.previewPrimary === 'string') setPreviewPrimary(data.previewPrimary)
    if (data.previewSecondary && typeof data.previewSecondary === 'string') setPreviewSecondary(data.previewSecondary)

    const lightSource = (data.light || data.lightPalette) as Record<string, string> | undefined
    const darkSource = (data.dark || data.darkPalette) as Record<string, string> | undefined

    if (lightSource && typeof lightSource === 'object') {
      setLightPalette((prev) => {
        const updated = { ...prev }
        for (const key of Object.keys(updated) as (keyof ThemePaletteDto)[]) {
          if (lightSource[key] && typeof lightSource[key] === 'string') {
            updated[key] = lightSource[key]
          }
        }
        return updated
      })
    }
    if (darkSource && typeof darkSource === 'object') {
      setDarkPalette((prev) => {
        const updated = { ...prev }
        for (const key of Object.keys(updated) as (keyof ThemePaletteDto)[]) {
          if (darkSource[key] && typeof darkSource[key] === 'string') {
            updated[key] = darkSource[key]
          }
        }
        return updated
      })
    }
  }

  const handleImportJson = (jsonText: string) => {
    setImportError(null)
    try {
      const parsed = JSON.parse(jsonText)
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          setImportError('JSON array is empty')
          return
        }
        // Use the first theme in the array
        applyImportedTheme(parsed[0])
        if (parsed.length > 1) {
          setImportError(null) // Clear — will show info via onImportMultiple
        }
      } else if (typeof parsed === 'object' && parsed !== null) {
        applyImportedTheme(parsed)
      } else {
        setImportError('JSON must be a theme object or an array of theme objects')
      }
    } catch {
      setImportError('Invalid JSON format')
    }
  }

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (typeof text === 'string') {
        handleImportJson(text)
      }
    }
    reader.readAsText(file)
    // Reset so the same file can be selected again
    event.target.value = ''
  }

  const handlePasteImport = async () => {
    try {
      const text = await navigator.clipboard.readText()
      handleImportJson(text)
    } catch {
      setImportError('Failed to read clipboard. Try pasting JSON manually.')
    }
  }

  const handleNameChange = (value: string) => {
    setName(value)
    if (!isEdit && !slugManuallyEdited) {
      setSlug(slugify(value))
    }
  }

  const handleSlugChange = (value: string) => {
    setSlug(value)
    setSlugManuallyEdited(true)
  }

  const updateLightPalette = (key: keyof ThemePaletteDto, value: string) => {
    setLightPalette((prev) => ({ ...prev, [key]: value }))
  }

  const updateDarkPalette = (key: keyof ThemePaletteDto, value: string) => {
    setDarkPalette((prev) => ({ ...prev, [key]: value }))
  }

  const handleCopyLightToDark = () => {
    setDarkPalette({ ...lightPalette })
  }

  const handleSave = () => {
    onSave({
      name,
      slug,
      description,
      isDefault,
      sortOrder,
      previewPrimary,
      previewSecondary,
      light: lightPalette,
      dark: darkPalette,
    })
  }

  const renderPaletteSection = (
    title: string,
    palette: ThemePaletteDto,
    updater: (key: keyof ThemePaletteDto, value: string) => void,
  ) => {
    const groups = [...new Set(PALETTE_FIELDS.map((f) => f.group))]
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        {groups.map((group) => (
          <Box key={group} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">{group}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
              {PALETTE_FIELDS.filter((f) => f.group === group).map((field) => (
                <ThemeColorField
                  key={field.key}
                  label={field.label}
                  value={palette[field.key]}
                  onChange={(v) => updater(field.key, v)}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Theme' : 'Create Theme'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {importError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>{importError}</Alert>}

        {/* Import Section */}
        {!isEdit && (
          <Box sx={{ mb: 2, mt: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Import from JSON</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileUpload />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload File
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handlePasteImport}
              >
                Paste from Clipboard
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleFileImport}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Accepts a single theme object or an array (first theme will be used).
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: isEdit ? 1 : 0 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            fullWidth
            required
          />
        </Box>

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <TextField
            label="Sort Order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
            size="small"
            sx={{ width: 120 }}
          />
          <FormControlLabel
            control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
            label="Default Theme"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview Colors</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <ThemeColorField label="Preview Primary" value={previewPrimary} onChange={setPreviewPrimary} />
          <ThemeColorField label="Preview Secondary" value={previewSecondary} onChange={setPreviewSecondary} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: previewPrimary, border: '1px solid', borderColor: 'divider' }} />
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: previewSecondary, border: '1px solid', borderColor: 'divider' }} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {renderPaletteSection('Light Palette', lightPalette, updateLightPalette)}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Divider sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyLightToDark}
          >
            Copy Light to Dark
          </Button>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {renderPaletteSection('Dark Palette', darkPalette, updateDarkPalette)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !name.trim() || !slug.trim()}>
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
